import PlayClient from '@empathybroker/tsclient-play';
import { EndpointEnum } from '@empathybroker/tsclient-play/dist/enums/endpoint.enum';
import { SortDirectionEnum as PlaySortDirectionEnum } from '@empathybroker/tsclient-play/dist/enums/sort-direction.enum';
import { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance';
import { Page as PlayPage } from '@empathybroker/tsclient-play/dist/models/page';
import { RequestFilters as PlayFilters } from '@empathybroker/tsclient-play/dist/models/request-filters';
import { SchedulableRequestFilters } from '@empathybroker/tsclient-play/dist/models/schedulable-request-filters';
import { Sort as PlaySort } from '@empathybroker/tsclient-play/dist/models/sort';
import { UsersClient } from '@empathybroker/tsclient-users';
import { Page as UsersPage } from '@empathybroker/tsclient-users/dist/models/page';

import { ActionContext } from 'vuex';

import { CONTAINER, TYPES } from '../../../config';

import {
  ActiveFilterEnum,
  CrudDataTypeEnum,
  DomainEnum,
  EnabledFilterEnum,
  FilterIdEnum,
  FilterTypeEnum,
  SortDirectionEnum
} from '../../../enums';

import {
  ActionClass,
  ContextState,
  CrudState,
  Dictionary,
  Filter,
  Pagination,
  RegularFilter,
  RegularFilterSelectedValues,
  Sort
} from '../../../models';

import { AUTH_CONSTS } from '../../../store/auth/auth.consts';
import { CONFIG_CONSTS } from '../../../store/config/config.consts';
import { CONTEXT_CONSTS } from '../../../store/context/context.consts';

export class FetchDataAction implements ActionClass {

  public static execute({ rootGetters }: ActionContext<CrudState, any>, payload: {
    dataType: CrudDataTypeEnum,
    filters?: Dictionary<Filter>,
    requestFilters?: any,
    pagination?: Pagination,
    sort?: Sort,
    query?: string,
    strictMatching?: boolean
  }): Promise<any> {
    const context: ContextState = rootGetters[`${ CONTEXT_CONSTS.moduleName }/${ CONTEXT_CONSTS.getters.context }`];
    const serviceClient = FetchDataAction.getServiceClient(payload.dataType, context);
    const authToken: string = rootGetters[`${ AUTH_CONSTS.moduleName }/${ AUTH_CONSTS.getters.token }`];
    if (payload.dataType === CrudDataTypeEnum.USERS) {
      return serviceClient.list(authToken, (!!payload.pagination ? FetchDataAction.getUsersPage(payload.pagination) : undefined));
    }
    const instanceConfig: Instance = rootGetters[`${ CONFIG_CONSTS.moduleName }/${ CONFIG_CONSTS.getters.currentInstance }`];
    const toolsConfig = (!!instanceConfig ? instanceConfig.tools : undefined);
    let toolConfig = Object.assign({}, (!!toolsConfig && toolsConfig.default), (!!toolsConfig && toolsConfig[payload.dataType]));
    toolConfig = {
      ...toolConfig,
      pivotIds: (toolConfig.pivotIds || [])
    };
    const endpoint: EndpointEnum = FetchDataAction.getPlayEndpointFromDomain(context.domain);
    const playFilters = (!!payload.filters ? FetchDataAction.getPlayFilters(payload.filters, toolConfig.pivotIds) : undefined);
    return serviceClient.list(
      authToken,
      payload.dataType,
      Object.assign({}, playFilters, payload.requestFilters),
      (!!payload.pagination ? FetchDataAction.getPlayPage(payload.pagination) : undefined),
      (!!payload.sort ? FetchDataAction.getPlaySort(payload.sort) : undefined),
      payload.query,
      payload.strictMatching,
      endpoint
    );
  }

  public static getPlayEndpointFromDomain(domain: DomainEnum): EndpointEnum {
    return {
      [DomainEnum.SEARCH]: EndpointEnum.SEARCH,
      [DomainEnum.BROWSE]: EndpointEnum.BROWSE
    }[domain];
  }

  public static getServiceClient(dataType: CrudDataTypeEnum, context: ContextState): any {
    if (dataType === CrudDataTypeEnum.USERS) {
      return CONTAINER.get<UsersClient>(TYPES.UsersClient);
    }
    return CONTAINER.get<(context: ContextState) => PlayClient>(TYPES.PlayClient)(context);
  }

  public static getPlayPage(pagination: Pagination): PlayPage {
    return {
      start: ((pagination.page - 1) * pagination.itemsPerPage),
      rows: pagination.itemsPerPage
    };
  }

  public static getPlaySort(sort: Sort): PlaySort {
    if (!!sort.field && !!sort.direction) {
      return {
        field: sort.field,
        direction: {
          [SortDirectionEnum.ASCENDING]: PlaySortDirectionEnum.ASCENDING,
          [SortDirectionEnum.DESCENDING]: PlaySortDirectionEnum.DESCENDING
        }[sort.direction]
      };
    }
  }

  private static getUsersPage(pagination: Pagination): UsersPage {
    return {
      start: ((pagination.page - 1) * pagination.itemsPerPage),
      rows: pagination.itemsPerPage
    };
  }

  private static getPlayFilters(filters: Dictionary<Filter>, pivotsIds: string[]): PlayFilters {
    const playFilters: PlayFilters = {
      pivots: {},
      filters: {}
    };
    Object.values(filters)
      .filter((filter: Filter) =>
        ((filter.config.type === FilterTypeEnum.REGULAR) && !!(filter.selectedValues as RegularFilterSelectedValues).length))
      .forEach((filter: RegularFilter) => {
        if (filter.config.id === FilterIdEnum.ACTIVE) {
          (playFilters as SchedulableRequestFilters).active = (filter.selectedValues[0] === ActiveFilterEnum.ACTIVE);
        } else if (filter.config.id === FilterIdEnum.ENABLED) {
          playFilters.enabled = (filter.selectedValues[0] === EnabledFilterEnum.ENABLED);
        } else {
          const filterId = (filter.config.toolingId || filter.config.id);
          const filterValue = filter.selectedValues.map((selectedValue: string) => {
            const selectedValueConfig = filter.config.values.find((valueConfig) => (valueConfig.id === selectedValue));
            return (selectedValueConfig.taggingId || selectedValueConfig.id);
          });
          if (pivotsIds.includes(filter.config.id)) {
            playFilters.pivots[filterId] = filterValue;
          }
          playFilters.filters[filterId] = filterValue;
        }
      });
    return playFilters;
  }

}
